import React, { memo, ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import styled from 'styled-components';

/**
 * Stand-alone KPI card and chart-card shells lifted out of PandaDocAnalytics
 * so the new Finance Dashboard (and any future analytics page) can reuse them
 * without a cross-page import. Identical visual tokens — copy of the original
 * styled bits — kept colocated so future redesigns touch one file.
 */

interface IKpiDelta {
  /** Numeric delta (current − previous) used to pick colour and arrow. */
  value: number;
  /** Human-readable label, e.g. «+12% vs prev» — rendered as-is. */
  label: string;
  /** If true, a negative delta is the «good» direction (e.g. expenses ↓). */
  inverse?: boolean;
}

interface IKpiCardProps {
  label: string;
  value: ReactNode;
  subLabel?: ReactNode;
  accent?: string;
  delta?: IKpiDelta;
  /** Optional leading lucide icon rendered before the label (decorative). */
  icon?: LucideIcon;
  /** Phase 13: optional click handler — turns the card into a deep-link
   *  to the matching list view (e.g. «Reconcile queue» → /finances/invoices). */
  onClick?: () => void;
}

const deltaColor = (value: number, inverse?: boolean) => {
  if (value === 0) return '#94a3b8';
  const good = inverse ? value < 0 : value > 0;
  return good ? '#16a34a' : '#dc2626';
};

const deltaArrow = (value: number) => {
  if (value === 0) return '·';
  return value > 0 ? '↑' : '↓';
};

export const KpiCard = memo(
  ({
    label,
    value,
    subLabel,
    accent,
    delta,
    icon: Icon,
    onClick,
  }: IKpiCardProps) => {
    const body = (
      <>
        <KpiLabel>
          {Icon ? <Icon size={12} strokeWidth={2} /> : null}
          {label}
        </KpiLabel>
        <KpiValue accent={accent}>{value}</KpiValue>
        {delta !== undefined && (
          <KpiDelta color={deltaColor(delta.value, delta.inverse)}>
            {deltaArrow(delta.value)} {delta.label}
          </KpiDelta>
        )}
        {subLabel !== undefined && <KpiSubLabel>{subLabel}</KpiSubLabel>}
      </>
    );
    return onClick ? (
      <KpiCardButton type="button" onClick={onClick} clickable>
        {body}
      </KpiCardButton>
    ) : (
      <KpiCardBox>{body}</KpiCardBox>
    );
  },
);

interface IChartCardProps {
  title: string;
  dotColor?: string;
  meta?: ReactNode;
  legend?: ReactNode;
  tall?: boolean;
  footer?: ReactNode;
  /** Optional leading lucide icon rendered before the title (decorative). */
  icon?: LucideIcon;
  children: ReactNode;
}

export const ChartCard = memo(
  ({
    title,
    dotColor,
    meta,
    legend,
    tall,
    footer,
    icon: Icon,
    children,
  }: IChartCardProps) => (
    <Card>
      <CardHeader>
        <CardTitle>
          {Icon ? <Icon size={14} strokeWidth={2} /> : null}
          {dotColor && <Dot color={dotColor} />}
          {title}
        </CardTitle>
        {legend}
        {meta !== undefined && <CardMeta>{meta}</CardMeta>}
      </CardHeader>
      <ChartWrap tall={tall}>{children}</ChartWrap>
      {footer !== undefined && <FooterNote>{footer}</FooterNote>}
    </Card>
  ),
);

/* ----- visual tokens (kept in-file to keep this molecule standalone) ----- */

const KpiCardBox = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
`;

/** Same visuals as `KpiCardBox` but renders as a native button so the card
 *  can be clicked into a related list view (Phase 13 «Reconcile queue» KPI). */
const KpiCardButton = styled.button<{ clickable?: boolean }>`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    ${({ clickable }) =>
      clickable
        ? 'border-color: #cbd5f5; box-shadow: 0 1px 4px rgba(15,23,42,0.06);'
        : ''}
  }
`;

const KpiLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const KpiValue = styled.div<{ accent?: string }>`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
  color: ${({ accent }) => accent || '#0f172a'};
`;

const KpiSubLabel = styled.div`
  color: #5b6775;
  font-size: 11px;
  margin-top: 2px;
`;

const KpiDelta = styled.div<{ color: string }>`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ color }) => color};
  letter-spacing: -0.01em;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: ${({ color }) => color};
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #5b6775;
  font-variant-numeric: tabular-nums;
`;

const ChartWrap = styled.div<{ tall?: boolean }>`
  position: relative;
  height: ${({ tall }) => (tall ? '240px' : '200px')};
`;

const FooterNote = styled.div`
  font-size: 11px;
  color: #5b6775;
  margin-top: 8px;
`;

export const KpiGrid = styled.div<{ columns: number }>`
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;
